import {
  BillingPlansResponseSchema,
  QuoteInputSchema,
  QuoteResponseSchema,
  CheckoutInputSchema,
  CheckoutResponseSchema,
  BillingHistoryResponseSchema,
  type BillingPlansResponse,
  type QuoteInput,
  type QuoteResponse,
  type CheckoutInput,
  type CheckoutResponse,
  type BillingHistoryResponse,
} from '../contracts/billing';
import type { UserRegion } from '../domain/onboarding-vocabularies';
import type { RequestFn } from './client';

/**
 * Namespace `client.billing` — транспорт биллинга (Блок 11, экран 2.11.3,
 * ADR-0093). **ТОЛЬКО транспорт.** Все эндпоинты приватные (`auth: true`) —
 * пользователь и его регион берутся бэком из JWT (P-006/P-007).
 *
 * **ИНВАРИАНТ ДЕНЕГ (P-006):** `quote`/`checkout` шлют только `planCode` +
 * `promoCode?` — суммы НЕТ в теле. `safeParse` ДО сети (default-strip) гарантирует,
 * что `amount`/`currency`/`provider`, даже если попали в объект-аргумент, срезаны
 * и НЕ уходят на провод. Сумму/скидку и провайдера определяет бэк (P-003).
 *
 * **Out of scope (явно):**
 *   - Реальный билинг-бэк (Edge-Worker: расчёт суммы P-003, hard-cap P-004,
 *     провайдеры Kaspi/ЮKassa, webhook + подпись P-005) — Фаза билинг (sandbox,
 *     P-024). 11.C — MSW-моки (plans/quote/history); checkout-мока в 11.C НЕТ.
 *   - `client.billing.checkout` зафиксирован для бэка, но экран 2.11.3 его НЕ
 *     дёргает: кнопка «Оплатить/Продлить» = заглушка «переход к оплате».
 *
 * Относительные пути (P-013), типизированный клиент (P-012), Zod-валидация
 * каждого ответа (контракт-drift ловится через PARSE_ERROR в client.ts).
 */
export interface BillingApi {
  /** GET /api/billing/plans?region — тарифы региона (месячная → массив из 1). */
  plans(region: UserRegion): Promise<BillingPlansResponse>;
  /**
   * POST /api/billing/quote — промо-preview (вар.А). Тело БЕЗ суммы (P-006);
   * `finalAmount`/`discountPercent`/`promoApplied` считает бэк.
   * @throws `z.ZodError` если client-side валидация не прошла (нет сетевого вызова).
   */
  quote(input: QuoteInput): Promise<QuoteResponse>;
  /**
   * POST /api/billing/checkout — создать платёж (редирект на провайдера). Тело
   * БЕЗ суммы (P-006); провайдер выбирает сервер по region из JWT.
   * **11.C — НЕ вызывается (кнопка = заглушка).** Зафиксирован для билинг-бэка.
   * @throws `z.ZodError` если client-side валидация не прошла.
   */
  checkout(input: CheckoutInput): Promise<CheckoutResponse>;
  /** GET /api/billing/history — пагинированная история платежей. */
  history(query?: { cursor?: string; limit?: number }): Promise<BillingHistoryResponse>;
}

export function createBillingApi(request: RequestFn): BillingApi {
  return {
    plans: (region) =>
      request({
        method: 'GET',
        path: '/api/billing/plans',
        query: { region },
        responseSchema: BillingPlansResponseSchema,
        auth: true,
      }),

    quote: (input) => {
      // P-006/P-009: safeParse ДО сети — лишние поля (amount/currency/price) срезаны.
      const parsed = QuoteInputSchema.safeParse(input);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/billing/quote',
        body: parsed.data,
        responseSchema: QuoteResponseSchema,
        auth: true,
      });
    },

    checkout: (input) => {
      // P-006/P-009: safeParse ДО сети — суммы/провайдера в теле нет.
      const parsed = CheckoutInputSchema.safeParse(input);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/billing/checkout',
        body: parsed.data,
        responseSchema: CheckoutResponseSchema,
        auth: true,
      });
    },

    history: (query) =>
      request({
        method: 'GET',
        path: '/api/billing/history',
        query: { cursor: query?.cursor, limit: query?.limit },
        responseSchema: BillingHistoryResponseSchema,
        auth: true,
      }),
  };
}
